import { useEffect, useState } from "react";
import { Bell, X, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DISMISS_KEY = "popu-push-dismissed";
const ASKED_KEY = "popu-push-asked";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export default function NotificationPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [iosNeedsInstall, setIosNeedsInstall] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

    // iOS Safari needs the app installed as PWA first
    if (isIOS && !isStandalone) {
      setIosNeedsInstall(true);
      setTimeout(() => setVisible(true), 1500);
      return;
    }

    // Modern browsers: need SW + PushManager + Notification
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    if (Notification.permission === "granted") return;
    if (Notification.permission === "denied") return;

    setTimeout(() => setVisible(true), 1200);
  }, []);

  const subscribe = async () => {
    if (iosNeedsInstall) {
      toast.info("Tocá Compartir → Añadir a inicio para instalar la app y activar notificaciones");
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      localStorage.setItem(ASKED_KEY, "1");
      if (perm !== "granted") {
        toast.error("Permiso denegado");
        setVisible(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const r = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(r.data.public_key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      toast.success("¡Listo! Te vamos a avisar de las breaking news");
      localStorage.setItem(DISMISS_KEY, "1");
      setVisible(false);
    } catch (err) {
      toast.error("No se pudieron activar las notificaciones");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 md:left-auto md:right-4 md:w-[380px] z-40 rounded-sm border shadow-2xl p-4 flex items-center gap-3 animate-fade-up"
      style={{
        background: "var(--bg)",
        borderColor: "var(--accent)",
        bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
      }}
      data-testid="notification-prompt"
    >
      <div className="w-10 h-10 rounded-sm accent-bg text-white flex items-center justify-center shrink-0">
        {iosNeedsInstall ? <Smartphone size={20} /> : <Bell size={20} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm leading-tight">
          {iosNeedsInstall ? "Activá notificaciones en iOS" : "Activá notificaciones"}
        </p>
        <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>
          {iosNeedsInstall
            ? "Compartir → Añadir a inicio. Después abrí la app desde el ícono."
            : "Enterate al toque de las breaking news."}
        </p>
      </div>
      {!iosNeedsInstall && (
        <button onClick={subscribe} disabled={busy} className="btn-accent text-xs px-3 py-2" data-testid="notification-prompt-accept">
          {busy ? "..." : "Activar"}
        </button>
      )}
      <button onClick={dismiss} className="p-1 hover:opacity-70" data-testid="notification-prompt-dismiss">
        <X size={16} />
      </button>
    </div>
  );
}
